import {
  GraphComponent,
  GraphItemTypes,
  GraphViewerInputMode,
  GroupNodeStyle,
  IEdge,
  IGraph,
  IModelItem,
  INode,
  ITable,
  RectangleNodeStyle
} from '@yfiles/yfiles';

import { ValueStreamGraphModelManager } from './ValueStreamGraphModelManager';
import { getFlowNodes, getProductsForNode } from './graphUtils';
import { Pseudo3DNodeStyle } from './Pseudo3DNodeStyle.ts';

/**
 * Clears all highlights from nodes and edges in the graph
 * @param graphComponent The graph component to clear highlights from
 */
export function clearHighlights(graphComponent: GraphComponent | null): void {
  if (!graphComponent) return;

  const graph = graphComponent.graph;

  // Reset node styles
  graph.nodes.forEach((node) => {
    if (node.style instanceof RectangleNodeStyle || node.style instanceof GroupNodeStyle) {
      node.style.cssClass = '';
    } else if (node.style instanceof Pseudo3DNodeStyle) {
      node.style.wrappedNodeStyle.cssClass = '';
    }
  });

  // Reset edge styles
  graph.edges.forEach((edge) => {
    if (edge.style) {
      (edge.style as any).cssClass = '';
    }
  });

  // Update z-order and refresh the view
  if (graphComponent.graphModelManager instanceof ValueStreamGraphModelManager) {
    const graphModelManager = graphComponent.graphModelManager as ValueStreamGraphModelManager;
    graphModelManager.hoveredEdges.clear();
    graphModelManager.updateZOrder();
  }

  graphComponent.invalidate();
}

function dimNode(node: INode, graph: IGraph) {
  const isWrappedStyle = node.style instanceof Pseudo3DNodeStyle;
  let useStyle = node.style;
  if (isWrappedStyle) {
    useStyle = node.style.wrappedNodeStyle;
  }

  // Rectangle nodes are on the lowest level and PRODUCT nodes. All other nodes are group nodes.
  if (useStyle instanceof RectangleNodeStyle) {
    // Fade product nodes not in flow
    const newStyle = new RectangleNodeStyle({
      cornerSize: useStyle.cornerSize,
      stroke: useStyle.stroke,
      fill: useStyle.fill
    });
    if (node.tag?.type === 'PRODUCT') {
      newStyle.cssClass = 'faded-product-node';
    } else {
      newStyle.cssClass = 'faded-node';
    }
    if (isWrappedStyle) {
      graph.setStyle(node, new Pseudo3DNodeStyle(newStyle));
    } else {
      graph.setStyle(node, newStyle);
    }
  }
}

function highlightNode(node: INode, graph: IGraph) {
  let useStyle = node.style;
  if (node.style instanceof Pseudo3DNodeStyle) {
    useStyle = node.style.wrappedNodeStyle;
  }

  if (node.tag?.type === 'PRODUCT') {
    (useStyle as GroupNodeStyle).cssClass = 'highlighted-group-node';
  } else {
    if (useStyle instanceof RectangleNodeStyle) {
      useStyle.cssClass = 'highlighted-node';
    } else if (useStyle instanceof GroupNodeStyle) {
      if (graph.foldingView?.isInFoldingState(node)) {
        useStyle.cssClass = 'highlighted-group-node';
      }
    }
  }
}

/**
 * Highlights nodes and edges related to the hovered item
 * @param graphComponent The graph component containing the item
 * @param item The hovered item
 */
export function highlightItems(graphComponent: GraphComponent | null, item: IModelItem): void {
  if (!graphComponent) return;

  const graph = graphComponent.graph;
  const graphModelManager = graphComponent.graphModelManager as ValueStreamGraphModelManager;

  clearHighlights(graphComponent);

  if (item instanceof INode) {
    // Skip table nodes
    if (ITable.getTable(item)) {
      return;
    }

    // Get products and flow nodes/edges
    const products = getProductsForNode(graph, item);

    const flowNodesList = getFlowNodes(item, graph, products);

    const flowNodes = new Set(flowNodesList.toArray());
    const flowEdges = new Set<IEdge>();

    // Collect edges that are part of the flow
    flowNodes.forEach((node) => {
      graph.edgesAt(node).forEach((edge) => {
        if (
          flowNodes.has(edge.sourceNode!) &&
          flowNodes.has(edge.targetNode!) &&
          edge.tag &&
          products.has(edge.tag.product)
        ) {
          flowEdges.add(edge);
        }
      });
    });

    // Add the original hovered node to ensure it's included
    flowNodes.add(item);

    // Update the graph model manager with the hovered edges for z-ordering
    graphModelManager.hoveredEdges.clear();
    flowEdges.forEach((edge) => graphModelManager.hoveredEdges.add(edge));

    // Apply styles to nodes
    graph.nodes.forEach((node) => {
      if (
        node.style instanceof RectangleNodeStyle ||
        node.style instanceof GroupNodeStyle ||
        node.style instanceof Pseudo3DNodeStyle
      ) {
        if (!flowNodes.has(node)) {
          dimNode(node, graph);
        } else {
          highlightNode(node, graph);
        }
      }
    });

    // Apply styles to edges
    graph.edges.forEach((edge) => {
      if (!flowEdges.has(edge)) {
        (edge.style as any).cssClass = 'faded-edge';
      }
    });

    // Update z-order and refresh
    graphComponent.invalidate();
  }
}

/**
 * Sets up hover highlighting for a graph component
 * @param graphComponent The graph component to set up hover highlighting for
 * @param viewType The current view type
 * @returns A cleanup function to remove event listeners
 */
export function setupHoverHighlighting(
  graphComponent: GraphComponent | null,
  viewType: string
): () => void {
  if (!graphComponent) return () => {};

  const inputMode = graphComponent.inputMode as GraphViewerInputMode;
  if (!inputMode) return () => {};

  // Enable hover for nodes
  inputMode.itemHoverInputMode.hoverItems = GraphItemTypes.NODE;

  // Add hover event listener
  const hoverListener = (evt: any) => {
    const item = evt.item;
    if (!item || viewType === 'TABULAR') {
      clearHighlights(graphComponent);
      evt.handled = true;
      return;
    }
    evt.handled = true;
    highlightItems(graphComponent, item);
  };

  // Register the event listener
  inputMode.itemHoverInputMode.addEventListener('hovered-item-changed', hoverListener);

  // Clear highlights when view type changes to TABULAR
  if (viewType === 'TABULAR') {
    clearHighlights(graphComponent);
  }

  // Return cleanup function
  return () => {
    inputMode.itemHoverInputMode.removeEventListener('hovered-item-changed', hoverListener);
  };
}
